import CommentTextArea from '@components/RecipeNotes/_CommentTextArea';
import { NoteProps } from '@hooks/customer/useRecipeNotes';
import { useUpdateRecipeNote } from '@hooks/customer/useUpdateRecipeNote';
import { Formik, Form } from 'formik';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useCreateNote } from '@hooks/customer/useCreateNote';
import React, { Dispatch, SetStateAction } from 'react';
import { recipeNoteAdded, recipeNoteUpdated } from '@repo/ui/src/services/analytics';
import { SegmentRecipeProps } from './_RecipeNote';

interface FormProps {
  recipeId: string;
  note: NoteProps | null;
  setShowForm: Dispatch<SetStateAction<boolean>>;
  visible: boolean;
  segmentRecipe: SegmentRecipeProps;
}

const NoteForm = ({ recipeId, note, setShowForm, visible, segmentRecipe }: FormProps) => {
  const { t } = useTranslation();
  const { trigger: createNote, data } = useCreateNote({ recipeId });
  const { trigger: updateNote } = useUpdateRecipeNote({ recipeId });

  const handleSubmit = async (value: any) => {
    const comment = value.comment;
    if (note) {
      await updateNote({ comment, id: note.id });
      recipeNoteUpdated({
        ...segmentRecipe,
        note_id: note?.id,
        recipe_id: recipeId,
        featured_products: segmentRecipe.featuredProducts,
      });
    } else {
      await createNote({ comment });
      recipeNoteAdded({
        ...segmentRecipe,
        note_id: data?.id,
        recipe_id: recipeId,
        featured_products: segmentRecipe.featuredProducts,
      });
    }
    setShowForm(false);
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={{
        comment: note?.comment ?? '',
      }}
    >
      {({ isSubmitting, handleReset }) => (
        <Form data-testid="note-form">
          <CommentTextArea note={note} visible={visible} />
          <Button type="submit" disabled={isSubmitting} className="mr-6 mb-3 w-full sm:w-fit">
            {t('recipeNotesContent.submitButton', undefined, {
              default: 'Submit',
            })}
          </Button>
          <Button
            type="button"
            variant={ButtonVariant.secondary}
            disabled={isSubmitting}
            className="w-full sm:w-fit"
            onClick={() => {
              handleReset();
              setShowForm(false);
            }}
          >
            {t('recipeNotesContent.cancelButton', undefined, {
              default: 'Cancel',
            })}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default NoteForm;
